import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useNotifications } from '@/hooks/useNotifications';
import { FollowerNotificationItem } from './notifications/FollowerNotificationItem';
import { BadgeNotificationItem } from './notifications/BadgeNotificationItem';
import { ProposalNotificationItem } from './notifications/ProposalNotificationItem';
import { ProposalResultNotificationItem } from './notifications/ProposalResultNotificationItem';
import { CheckinNotificationItem } from './notifications/CheckinNotificationItem';
import { CheckinLikeNotificationItem } from './notifications/CheckinLikeNotificationItem';
import { ExpertReviewPendingNotificationItem } from './notifications/ExpertReviewPendingNotificationItem';
import { NotificationSkeleton, EmptyNotifications } from './notifications/NotificationUtils';

interface NotificationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsDialog({ open, onOpenChange }: NotificationsDialogProps) {
  const { notifications, loading, markAsRead } = useNotifications();

  // Mark read when the dialog CLOSES, so items stay visibly "new" while
  // the user is reading them (previously they were cleared on open).
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (open) {
      wasOpen.current = true;
    } else if (wasOpen.current) {
      wasOpen.current = false;
      markAsRead();
    }
  }, [open, markAsRead]);


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
            {notifications.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {notifications.length}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <NotificationSkeleton key={i} />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <EmptyNotifications />
          ) : (
            <div className="space-y-1">
              {notifications.map((notification) => (
                <NotificationItemRenderer
                  key={notification.id}
                  notification={notification}
                  onClose={() => onOpenChange(false)}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NotificationItemRenderer({ 
  notification, 
  onClose 
}: { 
  notification: ReturnType<typeof useNotifications>['notifications'][0];
  onClose: () => void;
}) {
  const isUnread = !notification.read_at;
  
  switch (notification.type) {
    case 'follower':
      return (
        <FollowerNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'badge':
      return (
        <BadgeNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'burger_suggestion':
    case 'place_suggestion':
      return (
        <ProposalNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'proposal_result':
      return (
        <ProposalResultNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'burger_checkin':
      return (
        <CheckinNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'checkin_like':
      return (
        <CheckinLikeNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    case 'expert_review_pending':
      return (
        <ExpertReviewPendingNotificationItem
          notification={notification}
          isUnread={isUnread}
          onClose={onClose}
        />
      );
    default:
      return null;
  }
}
